import useSearchableSelection from "hooks/useSearchableSelection";
import ModalWrapper from "components/modals/ModalWrapper";
import { Form } from "antd";
import TextInput from "components/inputs/TextInput";
import showSuccessMsg from "utils/showSuccessMsg";
import { useGetZonesQuery } from "apis/services/zone";
import SelectionInput from "components/inputs/SelectionInput";
import {
  useAddLocationMutation,
  useEditLocationMutation,
  useGetProjectsQuery,
} from "apis/services/locations";
import { AddNewLocationModalProps } from "./types";
import { useEffect } from "react";

export default function AddNewLocationModal({
  isVisible,
  setIsVisible,
  isUpdate,
  initialValues,
}: AddNewLocationModalProps) {
  const [form] = Form.useForm();
  const { data: zones } = useGetZonesQuery({ page_size: 100 });

  const {
    data: projects,
    selectedValue,
    handleSearch,
    handleChange,
  } = useSearchableSelection(useGetProjectsQuery);

  const zoneOptions = zones?.results?.map((item: any) => ({
    label: item.name,
    value: item.id,
  }));
  const projectIDsOptions = projects?.results?.map((item: any) => ({
    label: `${item.name} (${item.project_id})`,
    value: item.id,
    projectId: item.project_id,
  }));

  const [addLocation, { isLoading: isAddLocationLoading }] =
    useAddLocationMutation();
  const [editLocation, { isLoading: isEditLocationLoading }] =
    useEditLocationMutation();

  const getProjectOption = (projectId: string) =>
    projectIDsOptions?.find((item: any) => projectId === item.projectId);

  const addNewZone = (values: any) => {
    addLocation(values)
      .unwrap()
      .then(() => {
        setIsVisible(false);
        form.resetFields();
        showSuccessMsg({ msg: "Added new location successfully!" });
      });
  };
  const editLocationFN = (values: any) => {
    const request_values = {
      ...values,
      id: initialValues?.id,
    };
    editLocation(request_values)
      .unwrap()
      .then(() => {
        setIsVisible(false);
        form.resetFields();
        showSuccessMsg({ msg: "Edited location successfully!" });
      });
  };

  const submitForm = () => {
    form.submit();
  };
  useEffect(() => {
    if (isVisible && isUpdate && initialValues) {
      // Set basic fields immediately
      const basicValues = {
        ...initialValues,
        zone: initialValues?.zone?.id,
      };
      
      // If projects are loaded, also set project_id
      if (projects?.results?.length && initialValues.project_id) {
        const projectOption = getProjectOption(initialValues.project_id);
        console.log(projectOption, "project option found");
        if (projectOption) {
          basicValues.project_id = projectOption.value;
        }
      }
      
      form.setFieldsValue(basicValues);
    }
  }, [isVisible, isUpdate, initialValues, projects?.results, form]);

  return (
    <ModalWrapper
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      size="large"
      headerTitle={isUpdate ? "Edit Location" : "Add New Location"}
      onConfirm={submitForm}
      isLoading={isAddLocationLoading || isEditLocationLoading}
    >
      <Form
        form={form}
        onFinish={isUpdate ? editLocationFN : addNewZone}
        initialValues={isUpdate && initialValues ? {
          ...initialValues,
          zone: initialValues?.zone?.id,
        } : {}}
        layout="vertical"
        scrollToFirstError
        className="w-100"
      >
        <Form.Item
          name="name"
          label="Location Name"
          rules={[{ required: true }]}
        >
          <TextInput />
        </Form.Item>

        <Form.Item name="zone" label="Zone" rules={[{ required: true }]}>
          <SelectionInput options={zoneOptions} />
        </Form.Item>

        <Form.Item name="address" label="Address" rules={[{ required: true }]}>
          <TextInput />
        </Form.Item>

        <Form.Item
          name="latitude"
          label="Latitude"
          rules={[{ required: true }, { type: "number" }]}
        >
          <TextInput isNumberInput />
        </Form.Item>

        <Form.Item
          name="longitude"
          label="Longitude"
          rules={[{ required: true }, { type: "number" }]}
        >
          <TextInput isNumberInput />
        </Form.Item>
        <Form.Item
          name="project_id"
          label="Project ID"
          rules={[{ required: true }, { type: "number" }]}
        >
          <SelectionInput
            options={projectIDsOptions}
            onSearch={handleSearch}
            onChange={handleChange}
            value={selectedValue}
            showSearch
          />
        </Form.Item>

        <Form.Item
          name="radius"
          label="Radius"
          rules={[{ required: true }, { type: "number" }]}
        >
          <TextInput isNumberInput />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true }]}
        >
          <TextInput type="textarea" />
        </Form.Item>
      </Form>
    </ModalWrapper>
  );
}
